import { useMatch } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { Alert, Skeleton, Stack, Typography as T } from "@mui/material";
import CohortListCard from "cohorts/components/CohortListCard";
import CreateCohort from "cohorts/components/CreateCohort";
import useCohorts from "cohorts/hooks/useCohorts";
import useDomains from "cohorts/hooks/useDomains";
import { PlusIcon, CardsIcon, WrenchIcon } from "~/assets/icons";
import CardGrid from "~/components/CardGrid";

// TODO: refactor limit when working on pagination
const Skeletons = ({ limit = 20 }: { limit?: number }) => (
  <>
    {Array.from({ length: Math.min(limit, 8) }).map((_, i) => (
      <Skeleton key={i} variant="rounded" sx={{ height: 290, display: "flex" }} />
    ))}
  </>
);

const EmptyPlaceholder = () => {
  const showWelcome = !!useMatch("/cohorts");

  return (
    <Stack gap={3} alignItems="center" data-testid="cohort-list-empty">
      <CardsIcon />
      <Stack gap={1} alignItems="center">
        {showWelcome && <T variant="h6">Welcome to Cohort Explorer</T>}
        <T variant={showWelcome ? "body2" : "body1"}>To get started, create your first cohort.</T>
      </Stack>
      <CreateCohort
        callToAction={
          <LoadingButton startIcon={<PlusIcon />} variant="contained">
            New Cohort
          </LoadingButton>
        }
      />
    </Stack>
  );
};

const UnconfiguredState = () => {
  return (
    <Stack gap={3} alignItems="center" data-testid="cohort-explorer-unconfigured">
      <WrenchIcon width={54} color="inherit" />
      <Stack gap={1} alignItems="center">
        <T variant="h5" fontWeight={500} sx={{ opacity: 0.5 }}>
          Cohort Explorer Requires Configuration
        </T>
      </Stack>
      <T variant="body2" sx={{ opacity: 0.5 }}>
        Contact your system administrator for assistance.
      </T>
    </Stack>
  );
};

const CohortList = ({ limit }: { limit?: number }) => {
  // Pre-load domains
  const { data: domains, isLoading: loadingDomains, error: errorDomains } = useDomains();
  const { data: cohorts, isLoading, error } = useCohorts();
  // TODO: refactor below when backend support for pagination is completed
  const partialCohorts = cohorts?.slice(0, limit);
  const showEmpty = !error && !isLoading && cohorts?.length === 0;
  const showUnconfigured = !errorDomains && !loadingDomains && domains?.length === 0;

  if (error) {
    return (
      <Alert severity="error" variant="outlined" sx={{ mb: 2 }} data-testid="cohort-list-error">
        Unable to load cohorts
      </Alert>
    );
  }

  if (showUnconfigured) {
    return <UnconfiguredState />;
  }

  if (showEmpty) {
    return <EmptyPlaceholder />;
  }

  return (
    <CardGrid data-testid="cohort-list-grid">
      {isLoading ? (
        <Skeletons limit={limit} />
      ) : (
        partialCohorts?.map((cohort) => <CohortListCard key={cohort.id} cohort={cohort} />)
      )}
    </CardGrid>
  );
};

export default CohortList;
